<template>
  <div>
    <div class="payment-header">
      <h2 class="payment-title">Order your VR headset</h2>
    </div>
    <h3 class="payment-subtitle">Delivery address</h3>
    <FieldInput
      name="name"
      placeholder="e.g. Bob Smith"
      labelText="Full Name"
      :value="formData.name.value"
      :touched="formData.name.touched"
      :onBlur="() => updateForm('name', formData.name.value, true)"
      :onChange="(event) => updateForm('name', event.target.value, false)"
      :validator="(value) => validator['name'](value)"
    />
    <div :class="getAddressClass()">
      <label for="street-address" class="field-label">Street Address</label>
      <input
        tabindex="0"
        ref="streetAddressLookUp"
        class="field-input"
        type="text"
        id="street-address"
        placeholder="e.g. Building number and street name"
        :value="formData.address.value"
        :touched="formData.address.touched"
        @focus="() => updateForm('address', formData.address.value, true)"
        @blur="() => updateForm('address', formData.address.value, true)"
        @change="(event) => updateForm('address', event.currentTarget.value)"
        @input.once="autoCompleteService()"
      />
      <label
        v-if="
          !validator['address'](this.formData.address.value).valid &&
          this.formData.address.touched
        "
        class="field-status"
        ><CustomIcon name="warning" />Address is required</label
      >
    </div>
    <FieldInput
      name="apartment"
      labelText="Address Line 2 (optional)"
      placeholder="e.g. Unit/Suite/Apt number"
      :value="formData.apartment.value"
      :touched="formData.apartment.touched"
      :onBlur="() => updateForm('apartment', formData.apartment.value, true)"
      :onChange="(event) => updateForm('apartment', event.target.value, false)"
      :validator="(value) => validator['apartment'](value)"
    />
    <FieldInput
      name="locality"
      placeholder="e.g. Dallas"
      labelText="City"
      :value="formData.locality.value"
      :touched="formData.locality.touched"
      :onBlur="() => updateForm('locality', formData.locality.value, true)"
      :onChange="(event) => updateForm('locality', event.target.value, false)"
      :validator="(value) => validator['locality'](value)"
    />
    <FieldSelector
      labelText="Country"
      :options="countries"
      placeholder="Select Country"
      :value="formData.countryAlpha2.value"
      :touched="formData.countryAlpha2.touched"
      :onBlur="updateCountryField"
      :onChange="updateCountryField"
      :validator="(value) => validator['country'](value)"
    />
    <FieldSelector
      :disabled="!formData.countryId.value"
      placeholder="e.g. Texas"
      name="state-selector"
      type="selector"
      :autoComplete="false"
      labelText="State/Province"
      :options="states"
      :value="formData.stateId.value"
      :touched="formData.stateId.touched"
      :onBlur="() => updateForm('stateId', formData.stateId.value, true)"
      :onChange="(event) => updateForm('stateId', event.target.value, false)"
      :validator="(value) => validator['state'](value)"
    />
    <FieldInput
      name="postal"
      labelText="Zipcode/Postcode"
      placeholder="e.g. 73301"
      :value="formData.postal.value"
      :touched="formData.postal.touched"
      :onBlur="() => updateForm('postal', formData.postal.value, true)"
      :onChange="(event) => updateForm('postal', event.target.value, false)"
      :validator="(value) => validator['postal'](value)"
    />
    <FieldInput
      name="phone"
      labelText="Phone number (Required for shipping)"
      placeholder="e.g. +1 555 555 5555"
      :value="formData.phoneNumber.value"
      :touched="formData.phoneNumber.touched"
      :onBlur="
        () => updateForm('phoneNumber', formData.phoneNumber.value, true)
      "
      :onChange="
        (event) => updateForm('phoneNumber', event.target.value, false)
      "
      :validator="(value) => validator['phoneNumber'](value)"
    />
    <SummaryBox
      v-if="formData.headsetType.value !== 'none'"
      title="Pay Now"
      :listItems="getSummaryItems()"
      footerText="Cancel anytime."
    />
    <p class="payment-text">
      Prices in USD. This payment is separate to your subscription as it’s a
      one-off charge.
    </p>
    <p class="payment-text">
      We currently ship from the US and New Zealand. For overseas deliveries
      international shipping costs and timeframes will apply, as well as duty
      (where applicable).
    </p>
    <h3 class="payment-subtitle">Pay with</h3>
    <StripePaymentForm
      :onSubmit="
        (stripe, paymentElement) =>
          submitDataFromForm(stripe, paymentElement, 'mobileForm')
      "
      label="Card number"
      :isPaymentRequired="true"
      :fieldIsComplete="(value) => (this.cardDetailsAreCompleted = value)"
    >
      <CustomButton :disabled="isLoading">
        <span v-if="!isLoading">
          Pay for VR Headset - {{ getTotalCost().value }}</span
        >
        <LoadingIndicator v-else />
      </CustomButton>
    </StripePaymentForm>
    <p class="payment-text">
      Subscription to be paid separately, in the next step after successful
      purchase of the VR Headset
    </p>
  </div>
</template>
<script>
import FieldInput from "@/components/FieldInput.vue";
import FieldSelector from "@/components/FieldSelector.vue";
import CustomButton from "@/components/CustomButton.vue";
import StripePaymentForm from "@/components/StripePaymentForm.vue";
import SummaryBox from "@/components/SummaryBox.vue";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import classnames from "classnames";
import GooglePlaces from "@/services/google-places";
import { PAYMENT_FORMS, MODULES, UNITY_METHOD } from "@/config";
import { fillInAddress, mobilePaymentValidator } from "@/utils";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      validator: mobilePaymentValidator,
      cardDetailsAreCompleted: false,
    };
  },
  props: {
    updateFormState: Function,
    submitFormData: Function,
    formData: Object,
    isLoading: Boolean,
  },
  components: {
    FieldInput,
    FieldSelector,
    CustomButton,
    StripePaymentForm,
    SummaryBox,
    LoadingIndicator,
  },
  mounted() {
    window.addEventListener("DOMContentLoaded", function () {
      if (window.Unity !== undefined) {
        window.Unity.call(
          JSON.stringify({
            method: UNITY_METHOD.loaded,
          })
        );
      }
    });

    this.initGooglePlaces();
  },
  computed: {
    ...mapGetters(MODULES.PAYMENT, {
      orderSummary: "getOrderSummary",
    }),
    ...mapGetters(MODULES.COUNTRY, {
      countries: "getCountries",
      states: "getCountryStates",
    }),
  },
  methods: {
    getAddressClass() {
      return classnames("field", {
        "is-invalid":
          !this.validator["address"](this.formData.address.value).valid &&
          this.formData.address.touched,
      });
    },
    checkFormFilled() {
      this.updateForm("phoneNumber", this.formData.phoneNumber.value, true);
      this.updateForm("name", this.formData.name.value, true);
      this.updateForm("address", this.formData.address.value, true);
      this.updateForm("locality", this.formData.locality.value, true);
      this.updateForm("postal", this.formData.postal.value, true);
      this.updateForm("apartment", this.formData.apartment.value, true);
      this.updateForm("countryAlpha2", this.formData.countryAlpha2.value, true);
      this.updateForm("stateId", this.formData.stateId.value, true);
    },
    submitDataFromForm(stripe, paymentElement, formType) {
      this.checkFormFilled();
      if (!this.formIsValid()) {
        return;
      }
      this.submitFormData(stripe, paymentElement, formType);
    },
    updateForm(key, value, touched = false) {
      const { updateFormState } = this;

      updateFormState(PAYMENT_FORMS.mobile, key, value, touched);
    },
    updateCountryField(event) {
      const { updateForm } = this;
      const { dispatch } = this.$store;

      if (event) {
        event.preventDefault();
      }

      const countryObj = this.countries.find(
        (country) => event.target.value === country.value
      );

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, countryObj.id).then(
        () => {
          updateForm("countryId", countryObj.id, true);
          updateForm("countryAlpha2", event.target.value, true);
          updateForm("stateId", "", true); // Clear the stateId field
        }
      );
    },
    getSummaryItems() {
      const { orderSummary } = this;

      let summaryHeadsetPrice = 0,
        deliveryCosts = null;

      if (orderSummary) {
        const { item, delivery_method } = orderSummary;

        summaryHeadsetPrice = Number(item.price.amount);

        if (delivery_method) {
          deliveryCosts = Number(delivery_method.amount.amount);
        }
      }

      return [
        {
          id: "vr_headset_price",
          value: `$${summaryHeadsetPrice.toFixed(2)}`,
          label: "Vr Headset",
          rawValue: summaryHeadsetPrice,
        },
        {
          id: "shipping_price",
          value:
            deliveryCosts !== null
              ? `$${deliveryCosts.toFixed(2)}`
              : "To be calculated",
          label: "Shipping",
          rawValue: summaryHeadsetPrice,
        },
        { ...this.getTotalCost() },
      ];
    },
    getTotalCost() {
      let summaryTotal = 0;
      let value = null;

      if (this.orderSummary) {
        const { delivery_method } = this.orderSummary;

        summaryTotal = Number(this.orderSummary.total.amount);

        if (delivery_method) {
          value = summaryTotal;
        }
      }

      return {
        id: "total_price",
        label: "Total to pay now",
        value: `${value ? `$${value.toFixed(2)}` : "TBD"}`,
        rawValue: summaryTotal,
      };
    },
    initGooglePlaces() {
      this.googleService = new GooglePlaces();
      this.googleService.init();

      this.autoComplete = new this.googleService.service.Autocomplete(
        this.$refs.streetAddressLookUp,
        {
          types: ["geocode"],
        }
      );
    },
    autoCompleteService() {
      this.autoComplete.addListener("place_changed", () => {
        const results = fillInAddress(this.autoComplete);

        this.autoCompleteForm(results);
        this.updateForm("phoneNumber", this.formData.phoneNumber.value, true);
      });
    },
    autoCompleteForm(results) {
      const blackListedKeys = ["country", "name", "stateId", "phoneNumber"]; // todo: setup seperate validator for these keys.
      const keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      keys.forEach((key) => {
        if (properties(this.validator, key) && !blackListedKeys.includes(key)) {
          const replaceLocality = this.shouldReplaceLocality(key, results);

          this.updateForm(
            key,
            replaceLocality ? results.sublocality : results[key],
            false
          );
        }
      });

      this.updateCountryFromGoogle(results.countryAlpha, results?.state);
    },
    shouldReplaceLocality(key, results) {
      return key === "locality" && !results[key] && results.sublocality;
    },
    updateCountryFromGoogle(googleCountryAlpha, googleCountryState) {
      const findCountry = this.countries.find(
        (country) => googleCountryAlpha === country.value
      );

      this.updateForm("countryId", findCountry.id, false);
      this.updateForm("countryAlpha2", findCountry.value, false);

      this.getCountryStates(findCountry.id, googleCountryState);
    },
    getCountryStates(id, googleCountryState) {
      const { dispatch } = this.$store;

      dispatch(`${MODULES.COUNTRY}/getCountryStatesById`, id).then(() => {
        this.updateFormState(PAYMENT_FORMS.mobile, "stateId", "", false);

        this.updateCountryStateFromGoogle(googleCountryState);
      });
    },
    // use regex to remove special characters, accents and put in lowercase
    updateCountryStateFromGoogle(googleCountryState) {
      if (googleCountryState) {
        const normalizedGoogleCountryState = googleCountryState
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        const state = this.states.find(
          (cstate) =>
            cstate.label
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase() === normalizedGoogleCountryState.toLowerCase()
        );
        if (state) {
          this.updateForm("stateId", state.value.toString(), true);
        }
      } else {
        this.updateForm("stateId", "", false);
      }
    },
    formIsValid() {
      let keys = Object.keys(this.formData);

      const properties = (validator, key) =>
        Object.prototype.hasOwnProperty.call(validator, key);

      const formIsValid = !keys.some((key) => {
        if (properties(this.validator, key)) {
          if (key === "stateId" && this.countryHasNoStates()) {
            return false;
          }

          return !this.validator[key](this.formData[key].value).valid;
        }

        return false;
      });

      return formIsValid && this.cardDetailsAreCompleted;
    },
    countryHasNoStates() {
      const { country } = this.formData;

      if (country.value && this.countryStates.length < 1) {
        return true;
      }

      return false;
    },
  },
};
</script>
